import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { formatCapacity } from '@/lib/utils/format';

interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  data: any;
  type: 'risk' | 'category' | 'contract' | 'system';
}

export const DetailModal: React.FC<DetailModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  data,
  type
}) => {
  // State for expandable systems (used in contract type)
  const [expandedSystems, setExpandedSystems] = React.useState<Set<string>>(new Set());
  // State for expandable contracts
  const [expandedContracts, setExpandedContracts] = React.useState<Set<string>>(new Set());
  // State for expandable risks
  const [expandedRisks, setExpandedRisks] = React.useState<Set<string>>(new Set());

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Set overflow hidden on both html and body elements
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.documentElement.style.overflow = 'unset';
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const getSeverityIcon = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'high':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'medium':
        return <Info className="w-5 h-5 text-yellow-500" />;
      case 'low':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'best_practice':
        return <Info className="w-5 h-5 text-green-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'high':
        return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
      case 'medium':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'low':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      case 'best_practice':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      default:
        return 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800';
    }
  };

  const toggleRiskExpansion = (riskId: string) => {
    setExpandedRisks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(riskId)) {
        newSet.delete(riskId);
      } else {
        newSet.add(riskId);
      }
      return newSet;
    });
  };

  const renderContent = () => {
    if (type === 'risk') {
      return (
        <div className="space-y-4">
          {data?.items?.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No risks found for this filter
            </div>
          ) : (
            data?.items?.map((item: any, index: number) => {
              const isExpanded = expandedRisks.has(item.risk_id);
              const affectedSystemsCount = item.affected_systems?.length || 0;

              return (
                <motion.div
                  key={item.risk_id || index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className={`rounded-lg border-2 overflow-hidden ${getSeverityColor(item.severity)}`}
                >
                  {/* Risk Header - Clickable to expand/collapse */}
                  <button
                    onClick={() => toggleRiskExpansion(item.risk_id)}
                    className="w-full p-5 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors text-left"
                  >
                    <div className="flex items-start gap-3">
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 mt-1 ${
                          isExpanded ? 'rotate-90' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      {getSeverityIcon(item.severity)}
                      <div className="flex-1">
                        {/* Risk Title and Summary */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-900 dark:text-white text-lg mb-1">
                              {item.title || item.description}
                            </h4>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                {item.category}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Risk ID: {item.risk_id}
                              </span>
                              {affectedSystemsCount > 0 && (
                                <span className="text-xs px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 font-medium">
                                  {affectedSystemsCount} System{affectedSystemsCount !== 1 ? 's' : ''} Affected
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>

                  {/* Expandable Risk Details */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <div className="p-5">
                        {/* Affected Systems */}
                        {item.affected_systems && item.affected_systems.length > 0 && (
                          <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/10 rounded-md border border-red-200 dark:border-red-800">
                            <h5 className="text-sm font-semibold text-red-700 dark:text-red-300 mb-2">
                              Affected Systems ({item.affected_systems.length}):
                            </h5>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {item.affected_systems.map((system: any, idx: number) => (
                                <div
                                  key={idx}
                                  className="p-2 bg-white dark:bg-gray-800 rounded border border-red-100 dark:border-red-900"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <span className="font-medium text-gray-900 dark:text-white">
                                        {system.host_name}
                                      </span>
                                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        <span>Serial: {system.serial_number}</span>
                                        {system.cluster_name && (
                                          <span className="ml-3">Cluster: {system.cluster_name}</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Risk Information */}
                        {(item.impact_area || item.source_object_type || item.mitigation_category) && (
                          <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-md">
                            <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Risk Details:
                            </h5>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              {item.impact_area && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Impact Area:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                    {item.impact_area}
                                  </span>
                                </div>
                              )}
                              {item.source_object_type && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Source Type:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                    {item.source_object_type}
                                  </span>
                                </div>
                              )}
                              {item.mitigation_category && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Mitigation Type:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                    {item.mitigation_category}
                                  </span>
                                </div>
                              )}
                              {(item.has_um_fixit || item.has_cloud_manager_fixit) && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Automated Fix:</span>
                                  <span className="ml-2 font-medium text-green-600 dark:text-green-400">
                                    {item.has_cloud_manager_fixit ? 'Cloud Manager' : 'UM FixIt'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Full Description */}
                        {item.description && (
                          <div className="mb-3">
                            <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                              Description:
                            </h5>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {item.description}
                            </p>
                          </div>
                        )}

                        {/* Impact and Probability */}
                        {(item.impact || item.probability) && (
                          <div className="mb-3 grid grid-cols-2 gap-3">
                            {item.impact && (
                              <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                                <span className="text-xs font-medium text-red-700 dark:text-red-300">
                                  Impact:
                                </span>
                                <p className="text-sm text-red-600 dark:text-red-400">
                                  {item.impact}
                                </p>
                              </div>
                            )}
                            {item.probability && (
                              <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                                <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                                  Probability:
                                </span>
                                <p className="text-sm text-orange-600 dark:text-orange-400">
                                  {item.probability}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Corrective Actions */}
                        <div className="mt-3">
                          {item.corrective_action && (
                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800 mb-3">
                              <h5 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">
                                Corrective Action:
                              </h5>
                              <p className="text-sm text-blue-600 dark:text-blue-400">
                                {item.corrective_action}
                              </p>
                            </div>
                          )}

                          {/* Corrective Action Links */}
                          {item.corrective_action_links && item.corrective_action_links.length > 0 && (
                            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-md border border-indigo-200 dark:border-indigo-800">
                              <h5 className="text-sm font-semibold text-indigo-700 dark:text-indigo-300 mb-2">
                                Additional Resources:
                              </h5>
                              <div className="space-y-2">
                                {item.corrective_action_links.map((link: any, idx: number) => (
                                  <a
                                    key={idx}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
                                  >
                                    <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300">
                                      {link.action_type || 'LINK'}
                                    </span>
                                    <span className="underline">{link.display_name}</span>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Timestamp */}
                        {item.created_at && (
                          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Detected: {new Date(item.created_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      );
    }

    if (type === 'category') {
      // For category view, display full risk details filtered by category
      return (
        <div className="space-y-4">
          {data?.items?.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No issues found in this category
            </div>
          ) : (
            data?.items?.map((item: any, index: number) => (
              <motion.div
                key={item.risk_id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-5 rounded-lg border-2 ${getSeverityColor(item.severity)}`}
              >
                <div className="flex items-start gap-3">
                  {getSeverityIcon(item.severity)}
                  <div className="flex-1">
                    {/* Risk Title and Severity */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-bold text-gray-900 dark:text-white text-lg mb-1">
                          {item.title || item.description}
                        </h4>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            item.severity === 'CRITICAL' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            item.severity === 'HIGH' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                            item.severity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                            item.severity === 'LOW' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          }`}>
                            {item.severity}
                          </span>
                          {item.risk_id && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Risk ID: {item.risk_id}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Affected Systems */}
                    {item.affected_systems && item.affected_systems.length > 0 && (
                      <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/10 rounded-md border border-red-200 dark:border-red-800">
                        <h5 className="text-sm font-semibold text-red-700 dark:text-red-300 mb-2">
                          Affected Systems ({item.affected_systems.length}):
                        </h5>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {item.affected_systems.slice(0, 5).map((system: any, idx: number) => (
                            <div key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                              <span className="font-medium">{system.host_name}</span>
                              {system.serial_number && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                  (S/N: {system.serial_number})
                                </span>
                              )}
                              {system.cluster_name && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                  - {system.cluster_name}
                                </span>
                              )}
                            </div>
                          ))}
                          {item.affected_systems.length > 5 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              ... and {item.affected_systems.length - 5} more
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Risk Details */}
                    {(item.impact_area || item.mitigation_category) && (
                      <div className="mb-3 grid grid-cols-2 gap-3">
                        {item.impact_area && (
                          <div className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">Impact Area:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-white">
                              {item.impact_area}
                            </span>
                          </div>
                        )}
                        {item.mitigation_category && (
                          <div className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">Mitigation:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-white">
                              {item.mitigation_category}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    {item.description && (
                      <div className="mb-3">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </p>
                      </div>
                    )}

                    {/* Corrective Action */}
                    {item.corrective_action && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                        <h5 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">
                          Action:
                        </h5>
                        <p className="text-sm text-blue-600 dark:text-blue-400">
                          {item.corrective_action}
                        </p>
                      </div>
                    )}

                    {/* Additional Resources */}
                    {item.corrective_action_links && item.corrective_action_links.length > 0 && (
                      <div className="mt-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-md border border-indigo-200 dark:border-indigo-800">
                        <h5 className="text-sm font-semibold text-indigo-700 dark:text-indigo-300 mb-2">
                          Resources:
                        </h5>
                        <div className="space-y-1">
                          {item.corrective_action_links.map((link: any, idx: number) => (
                            <a
                              key={idx}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                            >
                              <span className="underline">{link.display_name}</span>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      );
    }

    if (type === 'contract') {
      console.log('Contract modal data:', data);

      const toggleContractExpansion = (contractNumber: string) => {
        setExpandedContracts(prev => {
          const newSet = new Set(prev);
          if (newSet.has(contractNumber)) {
            newSet.delete(contractNumber);
          } else {
            newSet.add(contractNumber);
          }
          return newSet;
        });
      };

      const toggleSystemExpansion = (serialNumber: string) => {
        setExpandedSystems(prev => {
          const newSet = new Set(prev);
          if (newSet.has(serialNumber)) {
            newSet.delete(serialNumber);
          } else {
            newSet.add(serialNumber);
          }
          return newSet;
        });
      };

      return (
        <div className="space-y-4">
          {/* Contracts Count Summary */}
          {(data?.total_contracts > 0 || data?.contracts?.length > 0) && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                  Active Contracts
                </h4>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-blue-700 dark:text-blue-300">
                    {data.total_contracts} Contracts
                  </span>
                  <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {data.total_systems} Systems
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Contract List */}
          {!data?.contracts || data.contracts.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {!data?.contracts ? 'Loading contracts...' : `No contracts found with ${data?.filter?.status || ''} status`}
            </div>
          ) : (
            data.contracts.map((contract: any, index: number) => {
              const isExpanded = expandedContracts.has(contract.contract_number);
              const totalCapacity = contract.systems?.reduce((sum: number, s: any) => sum + (s.capacity_tb || 0), 0) || 0;

              return (
                <motion.div
                  key={contract.contract_number || index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800"
                >
                  {/* Contract Header - Clickable to expand/collapse */}
                  <button
                    onClick={() => toggleContractExpansion(contract.contract_number)}
                    className="w-full p-5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 hover:from-gray-100 hover:to-gray-150 dark:hover:from-gray-650 dark:hover:to-gray-550 transition-colors text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <svg
                          className={`w-5 h-5 text-gray-600 dark:text-gray-300 transition-transform flex-shrink-0 ${
                            isExpanded ? 'rotate-90' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <div>
                          <h4 className="font-bold text-lg text-gray-900 dark:text-white">
                            Contract: {contract.contract_number}
                          </h4>
                          <p className="text-sm text-gray-700 dark:text-gray-200">
                            Type: {contract.contract_type} | Support: {contract.support_level}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right mr-2">
                          <p className="text-xs text-gray-600 dark:text-gray-300">Systems</p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            {contract.systems?.length || 0}
                          </p>
                        </div>
                        <div className="text-right mr-2">
                          <p className="text-xs text-gray-600 dark:text-gray-300">Capacity</p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            {formatCapacity(totalCapacity)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            contract.status === 'active'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : contract.status === 'expiring'
                              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {contract.status}
                          </span>
                          {contract.days_until_expiry !== null && contract.days_until_expiry !== undefined && (
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              contract.days_until_expiry <= 7
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : contract.days_until_expiry <= 30
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}>
                              P{contract.days_until_expiry}D days
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>

                  {/* Expandable Contract Details */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <div className="p-5">

                        {/* Contract Details Grid */}
                        <div className="grid grid-cols-2 gap-4 text-sm mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <div>
                            <p className="text-gray-600 dark:text-gray-300">Coverage Period</p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {contract.start_date && contract.end_date
                                ? `${new Date(contract.start_date).toLocaleDateString()} - ${new Date(contract.end_date).toLocaleDateString()}`
                                : contract.end_date
                                ? `Expires: ${new Date(contract.end_date).toLocaleDateString()}`
                                : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-300">Total Capacity</p>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {formatCapacity(totalCapacity)}
                            </p>
                          </div>
                        </div>

                        {/* Systems Section */}
                        {contract.systems && contract.systems.length > 0 && (
                          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                            <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                              Systems ({contract.systems.length})
                            </h5>
                            <div className="space-y-2">
                              {contract.systems.map((system: any) => (
                                <div key={system.serial_number} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleSystemExpansion(system.serial_number);
                                    }}
                                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors flex items-center justify-between text-left"
                                  >
                                    <div className="flex items-center gap-3">
                                      <svg
                                        className={`w-4 h-4 text-gray-400 transition-transform ${
                                          expandedSystems.has(system.serial_number) ? 'rotate-90' : ''
                                        }`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                      <span className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                                        {system.serial_number}
                                      </span>
                                      <span className="text-sm text-gray-600 dark:text-gray-300">
                                        ({system.host_name})
                                      </span>
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-300">
                                      {formatCapacity(system.capacity_tb || 0)}
                                    </span>
                                  </button>

                                  {/* Expandable System Details */}
                                  {expandedSystems.has(system.serial_number) && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: 'auto', opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.2 }}
                                      className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50"
                                    >
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                          <p className="text-gray-600 dark:text-gray-400">Host Name</p>
                                          <p className="font-medium text-gray-900 dark:text-white">
                                            {system.host_name}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-gray-600 dark:text-gray-400">Cluster</p>
                                          <p className="font-medium text-gray-900 dark:text-white">
                                            {system.cluster_name || 'Standalone'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-gray-600 dark:text-gray-400">System ID</p>
                                          <p className="font-medium text-gray-900 dark:text-white">
                                            {system.system_id}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-gray-600 dark:text-gray-400">Model</p>
                                          <p className="font-medium text-gray-900 dark:text-white">
                                            {system.model || 'N/A'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-gray-600 dark:text-gray-400">Capacity</p>
                                          <p className="font-medium text-gray-900 dark:text-white">
                                            {formatCapacity(system.capacity_tb || 0)}
                                          </p>
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      );
    }

    // Default content for other types
    return (
      <div className="space-y-4">
        {data?.items?.map((item: any, index: number) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
          >
            {/* Content for default items */}
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {item.description || 'No details available'}
            </p>
          </motion.div>
        ))}
        {!data?.items?.length && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No data available
          </div>
        )}
      </div>
    );
  };

  // Default system details section - commented out for future use
  /*
  const renderDefaultSystemDetails = () => {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data?.metrics?.map((metric: any, index: number) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="p-4 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-600"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {metric.label}
                </p>
                {metric.trend && (
                  metric.trend === 'up'
                    ? <TrendingUp className="w-4 h-4 text-green-500" />
                    : <TrendingDown className="w-4 h-4 text-red-500" />
                )}
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {metric.value}
              </p>
              {metric.change && (
                <p className={`text-xs mt-1 ${
                  metric.change > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {metric.change > 0 ? '+' : ''}{metric.change}% from last week
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    );
  };
  */

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="pointer-events-auto w-full max-w-6xl max-h-[85vh] bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
              {/* Header */}
              <div className="relative px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700">
                <div className="pr-10">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {title}
                  </h3>
                  {subtitle && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {subtitle}
                    </p>
                  )}
                </div>
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
                {renderContent()}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                <div className="flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DetailModal;